import React from 'react';
import styles from './StatsCard.module.scss';
import AppIcon from '../AppIcon/AppIcon';
import SkeltonLoader from '../SkeltonLoader/SkeltonLoader';
import { formatMinutes } from '@/utils/formatters';
import { createAnchorWrapper } from '@/components/school-management/Anchor/Anchor';

const SpanAnchor = createAnchorWrapper('span');

export interface StatsCardProps {
  value: string | number;
  label: string;
  percentage?: string;
  iconColor?: string;
  isTimeValue?: boolean;
  tooltip?: string;
  isTimeCard?: boolean;
  labelIcon?: keyof typeof import('@/school-management/utils/icons').icons;
  emptyMessage?: string;
  disableFallback?: boolean;
  isLoading?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = ({
  value,
  label,
  percentage,
  iconColor = '#185B97',
  isTimeValue = false,
  tooltip,
  isTimeCard = false,
  labelIcon,
  emptyMessage,
  disableFallback = false,
  isLoading = false,
}) => {
  const isZeroTimeString = (raw: string): boolean => {
    const normalized = raw.trim().replace(/\s+/g, ' ').toLowerCase();
    return normalized === '0m 0s';
  };

  const shouldShowFallback =
    !isLoading &&
    !disableFallback &&
    ((typeof value === 'number' && value <= 0) ||
      (typeof value === 'string' && isZeroTimeString(value)));

  const displayValue = shouldShowFallback
    ? emptyMessage
    : isTimeValue && typeof value === 'number'
      ? formatMinutes(value)
      : value;

  return (
    <div className={styles.statsCard}>
      <div className={styles.content}>
        <div className={styles.mainValue}>
          <div className={styles.valueWrapper}>
            {isLoading ? (
              <SkeltonLoader
                className={isTimeCard ? styles.skeletonValueTime : styles.skeletonValue}
              />
            ) : (
              <>
                <span
                  className={
                    shouldShowFallback
                      ? styles.fallbackText
                      : isTimeCard
                        ? styles.timeValue
                        : styles.value
                  }
                >
                  {displayValue}
                </span>
                {percentage && <span className={styles.percentage}>({percentage})</span>}
              </>
            )}
          </div>
        </div>
        <div className={styles.label}>
          {isLoading ? (
            <SkeltonLoader className={styles.skeletonLabel} />
          ) : (
            <>
              {labelIcon && <AppIcon icon={labelIcon} size={16} className={styles.labelIcon} />}
              {label}
              {tooltip && (
                <SpanAnchor
                  className={styles.help}
                  trigerType='hover'
                  autoClose={false}
                  stayInView={true}
                  tooltip={<div className={styles.tooltip}>{tooltip}</div>}
                  renderTooltipPosition={anchorPosition => {
                    return {
                      top: anchorPosition.top + anchorPosition.height + 5,
                      left: anchorPosition.left + anchorPosition.width + 5,
                    };
                  }}
                >
                  <AppIcon icon='helpBtn' />
                </SpanAnchor>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsCard;
